import { ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { isMac } from "../editor/find";
import { Popover } from "./Popover";
import { MAX_ZOOM, MIN_ZOOM } from "./PdfViewer";
import "./pdfzoom.css";

/** Zoom values that pdf.js works out from the pane's size. */
export const FIT_SCALES = ["page-width", "page-height", "page-fit", "auto"];

const MOD = isMac ? "⌘ " : "Ctrl ";
const LEVELS = [50, 75, 100, 150, 200, 400];

/**
 * The zoom button above the PDF and its menu: type a zoom, step it, fit the page, present,
 * or pick a set level. Ctrl/⌘ with + - 0 9 do the same while focus is in the PDF column.
 */
export function PdfZoomMenu({
  percent,
  canPresent,
  onZoomIn,
  onZoomOut,
  onScale,
  onPresent,
}: {
  percent: number;
  /** There's a PDF on screen. */
  canPresent: boolean;
  onZoomIn: () => void;
  onZoomOut: () => void;
  /** A pdf.js scale value: one of FIT_SCALES, or a factor such as "1.5". */
  onScale: (value: string) => void;
  onPresent: () => void;
}) {
  const button = useRef<HTMLButtonElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const actions = useRef({ onZoomIn, onZoomOut, onScale });
  actions.current = { onZoomIn, onZoomOut, onScale };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(isMac ? e.metaKey : e.ctrlKey) || e.altKey || e.defaultPrevented) return;
      const col = button.current?.closest(".pdf-col");
      const at = document.activeElement;
      if (!col || !(col.contains(at) || at?.closest(".zoom-menu"))) return;
      const a = actions.current;
      const run = { "=": a.onZoomIn, "+": a.onZoomIn, "-": a.onZoomOut, "0": () => a.onScale("page-width"), "9": () => a.onScale("page-height") }[e.key];
      if (!run) return;
      // Instead of the browser zooming the whole page.
      e.preventDefault();
      run();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Not on touch screens, where focusing would pop the keyboard up over the menu.
  useEffect(() => {
    if (open && matchMedia("(pointer: fine)").matches) {
      input.current?.focus({ preventScroll: true });
      input.current?.select();
    }
  }, [open]);

  const pick = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };

  return (
    <>
      <button
        ref={button}
        className={`zoom-btn${open ? " open" : ""}`}
        title="Zoom"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          setTyped(`${percent}%`);
          setOpen(!open);
        }}
      >
        {percent}%
        <ChevronDown size={13} />
      </button>
      {open && button.current && (
        <Popover anchor={button.current} align="end" className="zoom-menu" label="Zoom" onClose={() => setOpen(false)}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const n = parseFloat(typed);
              if (!(n > 0)) return;
              setOpen(false);
              onScale(String(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, n / 100))));
            }}
          >
            <input ref={input} value={typed} onChange={(e) => setTyped(e.target.value)} inputMode="decimal" aria-label="Zoom level" />
          </form>
          <div className="zoom-sep" />
          <button onClick={pick(onZoomIn)}>
            Zoom in <kbd>{MOD}+</kbd>
          </button>
          <button onClick={pick(onZoomOut)}>
            Zoom out <kbd>{MOD}-</kbd>
          </button>
          <button onClick={pick(() => onScale("page-width"))}>
            Fit to width <kbd>{MOD}0</kbd>
          </button>
          <button onClick={pick(() => onScale("page-height"))}>
            Fit to height <kbd>{MOD}9</kbd>
          </button>
          <div className="zoom-sep" />
          <button
            onClick={pick(onPresent)}
            disabled={!canPresent || !document.fullscreenEnabled}
            title={document.fullscreenEnabled ? "One page at a time, full screen. Arrow keys or a click change page; Esc ends it." : "This browser can't show the PDF full screen"}
          >
            Presentation mode
          </button>
          <div className="zoom-sep" />
          <div className="zoom-heading">Zoom to</div>
          {LEVELS.map((n) => (
            <button key={n} onClick={pick(() => onScale(String(n / 100)))}>
              {n}%
            </button>
          ))}
        </Popover>
      )}
    </>
  );
}
